import { useState, useEffect } from 'react';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { LandingPage } from '@leagueplatform/solutions-foundation';
import { queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import BackgroundDesktopImage from 'assets/pre-login-desktop.svg';
import BackgroundMobileImage from 'assets/pre-login-mobile.svg';
import LogoImage from 'assets/my-care-pilot-logo.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginErrorPage } from 'pages/errors/login-error/login-error.page';
import { useHasLoggedInBefore } from 'hooks/use-has-logged-in-before.hook';
import { clearCaregiverImpersonationState } from '@leagueplatform/caregiver';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';
import { QueryParamModal } from 'components/query-param-modal/query-param-modal.component';
import { SignInFooter } from './sign-in-footer.component';

const MOBILE_BACKGROUND_HEIGHT = '40dvh';

export const PreLoginLandingPage = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [hasLoginError, setHasLoginError] = useState(false);
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));
  const { hasLoggedInBefore, markLoggedInBefore } = useHasLoggedInBefore();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('error')) {
      clearCaregiverImpersonationState();
    }
  }, [searchParams]);

  if (hasLoginError)
    return (
      <LoginErrorPage
        onButtonClick={() => {
          // Full page reload so the Auth0 client reinitializes without stale impersonation params
          window.location.replace(APP_MANAGED_ROUTES.LANDING_PAGE);
        }}
      />
    );

  return (
    <>
      <ObservabilityErrorBoundary
        fallback={null}
        errorContext={{
          context: { Context: { location: 'QueryParamModal ErrorBoundary' } },
        }}
      >
        <QueryParamModal isPublic />
      </ObservabilityErrorBoundary>
      <LandingPage
        logo={{
          src: LogoImage,
          alt: formatMessage({ id: 'STR_APP_NAME' }),
        }}
        backgroundImage={{
          src: isMobile ? BackgroundMobileImage : BackgroundDesktopImage,
        }}
        mobileBackgroundImage={{
          height: MOBILE_BACKGROUND_HEIGHT,
          css: { backgroundPosition: 'center' },
        }}
        primaryButton={{
          buttonText: formatMessage({ id: 'STR_LOG_IN' }),
          onClick: () => navigate(APP_MANAGED_ROUTES.SIGN_IN),
          css: {
            width: '327px',
            maxWidth: '327px',
          },
        }}
        onAuthenticated={(appState) => {
          markLoggedInBefore();
          navigate(appState?.redirectPath || APP_MANAGED_ROUTES.HOME);
        }}
        onAuthError={() => setHasLoginError(true)}
        signInFooter={<SignInFooter showGuidance={!hasLoggedInBefore} />}
        shouldDisplayErrorCaption
      />
    </>
  );
};
